import { useEffect, useState } from "react";
import axios from "axios";
import AppLayout from "../AppLayout";
import { API } from "../apiConfig";
import {
  Package,
  Boxes,
  ReceiptText,
  Wallet,
  AlertTriangle,
  IndianRupee,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";

function Dashboard() {
  const [summary, setSummary] = useState({
    total_products: 0,
    total_stock: 0,
    total_invoices: 0,
    total_sales_amount: 0,
    pending_payments: 0,
    today_invoices: 0,
    today_sales: 0,
    sales_trend: [],
  });

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/summary`);
      setSummary(res.data.data || {});
    } catch (error) {
      console.log(error);
    }
  };

  const money = (value) => `₹ ${Number(value || 0).toFixed(2)}`;

  return (
    <AppLayout title="Dashboard" subtitle="Retail billing performance overview">
      <div className="eb-dashboard-page">
        <div className="eb-kpi-grid">
          <DashboardCard color="blue" title="Total Products" value={summary.total_products} icon={<Package />} />
          <DashboardCard color="orange" title="Total Invoices" value={summary.total_invoices} icon={<ReceiptText />} />
          <DashboardCard color="purple" title="Total Sales" value={money(summary.total_sales_amount)} icon={<Wallet />} />
        </div>

        <div className="eb-dashboard-summary-row">
          <div className="eb-today-summary-card">
            <div className="eb-chart-header">
              <div>
                <h3>Today’s Sales Summary</h3>
                <p>Today’s live billing records</p>
              </div>
            </div>

            <div className="eb-summary-list eb-summary-row-list">
              <SummaryItem icon={<IndianRupee />} label="Today’s Sales" value={money(summary.today_sales)} />
              <SummaryItem icon={<ReceiptText />} label="Today’s Bills" value={summary.today_invoices} />
              <SummaryItem icon={<AlertTriangle />} label="Pending Amount" value={money(summary.pending_payments)} />
              <SummaryItem icon={<TrendingUp />} label="Total Sale Amount" value={money(summary.total_sales_amount)} />
            </div>
          </div>
        </div>

        <div className="eb-dashboard-chart-row">
          <div className="eb-sales-chart-card">
            <div className="eb-chart-header">
              <div>
                <h3>Sales Analytics</h3>
                <p>Daily sales amount for the past week</p>
              </div>
              <span>Last 7 Days</span>
            </div>

            <div className="eb-chart-box">
              <ResponsiveContainer width="100%" height={330}>
                <BarChart data={summary.sales_trend || []} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.92} />
                      <stop offset="60%" stopColor="#fed7aa" stopOpacity={0.62} />
                      <stop offset="100%" stopColor="#ffffff" stopOpacity={0.38} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f7c5a6" />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 13, fontWeight: 600 }} />
                  <YAxis
                    yAxisId="left"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 13 }}
                    tickFormatter={(value) => `₹${value}`}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(34, 197, 94, 0.08)" }}
                    formatter={(value) => money(value)}
                    labelFormatter={(label) => `Day: ${label}`}
                    wrapperStyle={{ borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)" }}
                  />

                  <Bar
                    yAxisId="left"
                    dataKey="sales_amount"
                    name="Sales Amount"
                    fill="url(#salesGradient)"
                    radius={[12, 12, 0, 0]}
                    barSize={34}
                  >
                    <LabelList dataKey="sales_amount" formatter={(value) => `₹${Number(value).toFixed(0)}`} position="top" fill="#0f172a" style={{ fontSize: 12, fontWeight: 700 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function DashboardCard({ title, value, icon, color }) {
  return (
    <div className={`eb-kpi-card ${color}`}>
      <div className="eb-kpi-card-accent" />
      <div className="eb-kpi-card-main">
        <div className="eb-kpi-copy">
          <p>{title}</p>
          <h2>{value}</h2>
        </div>
        <div className={`eb-kpi-icon-box ${color}`}>
          <div className="eb-kpi-icon">{icon}</div>
        </div>
      </div>
    </div>
  );
}

function SummaryItem({ icon, label, value }) {
  return (
    <div className="eb-summary-item">
      <div className="eb-summary-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <b>{value}</b>
      </div>
    </div>
  );
}

export default Dashboard;